import {Model, ViewOptions} from 'backbone';
import * as d3 from 'd3';
import {ScaleLinear} from 'd3';
import $ from 'jquery';
import {ReactElement} from 'react';
import ReactDOM from 'react-dom';
import {debounce, DebouncedFunction} from 'ts-debounce';
import _ from 'underscore';

import * as alignmentviewutil from 'analyses/alignment/AlignmentViewUtil';
import {LineRanges} from 'analyses/alignment/GapSequenceWrapper';
import AlignmentTabTemplate from 'analyses/AlignmentTab';
import {shouldSuppressNotification} from 'BenchlingUtil';
import {BufferViewLayout} from 'buffers/BufferComps';
import PolymerBufferTabView from 'components/PolymerBufferTabView';
import AaSequenceAlignmentRowView from 'molbio/maps/alignment/AaSequenceAlignmentRowView';
import {MinimalAlignmentRowsView} from 'molbio/maps/alignment/PolymerAlignmentRowsView';
import {SequenceAlignmentRowView} from 'molbio/maps/alignment/SequenceAlignmentRowView';
import AlignmentSummaryMapView from 'molbio/maps/alignment/summary/AlignmentSummaryMapView';
import {ScrollData} from 'molbio/maps/linear/InfiniteScroller';
import {Line} from 'molbio/maps/linear/LinearLayout';
import Slice from 'molbio/maps/linear/Slice';
import Range from 'molbio/maps/selectables/Range';
import {Selection} from 'molbio/maps/selectables/Selection';
import Type from 'molbio/maps/Type';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';
import {TabState} from 'Navigation';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import setTestAttrs from 'test/lib/cypress/setTestAttrs';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {isAnArrowKey, isDownKey, isUpKey} from 'util/Keyboard';
import * as net from 'util/Net';

export interface ScrollAnimatorData {
  base: number;
}

interface PolymerAlignmentTabStateAttributes {
  slice: Slice | null;
  range: Range | null;
  loaded: boolean;
  selection: Selection;
  visibilityOptions: vo.VisibilityOptions;
  bufferViewLayout?: BufferViewLayout;
  scrollData: ScrollData | null;
}

// Merged interface that both sequence and protein alignment tab states must implement.
export interface PolymerAlignmentTabState<TRowState> extends TabState {
  get<K extends keyof PolymerAlignmentTabStateAttributes>(key: K): PolymerAlignmentTabStateAttributes[K];
  set<K extends keyof PolymerAlignmentTabStateAttributes>(
    attributeName: K,
    value: PolymerAlignmentTabStateAttributes[K],
    options?: Backbone.ModelSetOptions
  ): PolymerAlignmentTabState<TRowState>;
  set(obj: Partial<PolymerAlignmentTabStateAttributes>): PolymerAlignmentTabState<TRowState>;

  cleanUp(): void;
  getSubmapstates(): Array<TRowState>;
  getNameFromSubmapstate(rowState: TRowState): string;
  getLineRanges(rowState: TRowState): LineRanges;
  getCidForSubmapstate(rowState: TRowState): string | number;
  getMasterSubmapstateCid(): string | number;
  getSummaryMapLineLayout(): Line;
  setSummaryMapLineLayout({height, vMargin}: {height: number; vMargin: number}): void;
  getPolymerLength(): number;
  getSummaryMapVisibilityOptions(): vo.VisibilityOptions;
  getType(): Type;
  getSummaryType(): Type;
  fitSummaryTypeToWidth(chars: number, width: number, options?: {allowFractionalSpacing?: boolean}): void;
}

export default abstract class PolymerAlignmentTabView<
  TRowState,
  M extends PolymerAlignmentTabState<TRowState>,
> extends PolymerBufferTabView<M> {
  scrollAnimator!: ScrollAnimatorData;
  debouncedResizeContainer!: DebouncedFunction<() => void>;
  summaryMapView!: AlignmentSummaryMapView<TRowState>;
  rowsView?: MinimalAlignmentRowsView;
  topAlignmentView?: SequenceAlignmentRowView | AaSequenceAlignmentRowView;
  renderStatsView?(): ReactElement | null;
  getBanners?(): JSX.Element;
  private pendingFirstShow = true;

  static override initClass(): void {
    this.prototype.tagName = 'div';
    this.prototype.className = 'alignment-view buffer-tab';
    this.prototype.attributes = {};
    this.prototype.events = {
      // @ts-expect-error
      keydown: 'handleKeyDown',
    };
  }

  override initialize(options: ViewOptions<M>): void {
    super.initialize(_(options).extend({tabstate: this.model}));
    this.scrollAnimator = {base: 0};
    this.debouncedResizeContainer = debounce(() => {
      this.resizeContainer();
    }, 20);
  }

  abstract override addPolymersForSearch(): void;
  abstract override removePolymersForSearch(): void;
  abstract makeRowsView(): MinimalAlignmentRowsView;
  abstract makeSummaryMapView(): AlignmentSummaryMapView<TRowState>;
  abstract makeTopAlignmentRowView(): SequenceAlignmentRowView | AaSequenceAlignmentRowView;
  abstract getType(): Type;
  abstract setType(type: Type): void;
  abstract getPolymerLength(): number;
  abstract getDataTestAttrs(): DataTestAttrs;

  curScrollLeft(): number {
    return this.$el.find('.fixed-top').find('.content').scrollLeft();
  }

  getDefaultSpan(): number {
    const xInvFn = this.model.get('slice')!.get('xInv')!;
    return Math.floor(xInvFn(this.mapWidth()));
  }

  setMapDimensions(setStart: boolean = true): void {
    const xInvFn = this.model.get('slice')!.get('xInv');
    let start;
    if (xInvFn == null) {
      return;
    }
    if (setStart) {
      const left = this.curScrollLeft();
      start = Math.floor(xInvFn(left));
    } else {
      start = this.model.get('range')!.start();
    }

    const span = this.getDefaultSpan();
    const end = Math.min(this.getPolymerLength(), start + span);
    this.model.get('range')!.set({start, end, maxWidth: this.mapWidth()});
    this.model.get('range')!.correct();
  }

  handleArrowPress(e: JQueryEventObject): void {
    const focused = this.$el.find('.alignment-row:focus');
    if (focused.length === 0) {
      return;
    }

    if (isUpKey(e)) {
      const prev = focused.prev();
      if (prev.length > 0) {
        prev.focus();
      } else {
        focused.siblings().last().focus();
      }
    } else if (isDownKey(e)) {
      const next = focused.next();
      if (next.length > 0) {
        next.focus();
      } else {
        focused.siblings().first().focus();
      }
    }
  }

  handleKeyDown(e: JQueryEventObject): void {
    if (isAnArrowKey(e)) {
      this.handleArrowPress(e);
    }
  }

  resizeContainer(): void {
    // We want to allocate the available space between the warning banners, top alignment, the alignments, and summary view.
    // We have `totalHeight` space to allocate:
    const totalHeight = this.$el.outerHeight(true);

    // For now, we assume the summary view uses up as much space as it needs (it already optimizes for
    // minimal space usage), `bottom`, leaving us `middle` for any warning banners, the top alignment row
    // and the remaining alignments.
    const bottom = this.$('.fixed-bottom').outerHeight(true);
    const middle = totalHeight - bottom;

    // The top alignment can take up more than `middle` if it has lots of annotations. Therefore, we ensure
    // the the top alignment's height `top` leaves at least 75px for the remaining alignments... but never
    // spills out of the middle region (ie. when middle = 0). Include the possible warning section in this height
    const alignmentWarningHeight =
      this.$('.alignment-warning')
        .children() // on the first render, the outer div does not pick up the inner element's margin
        .outerHeight(true) ?? 0;
    const topAlignmentHeightAndWarning =
      this.$('.fixed-top .alignment-container').outerHeight(true) + alignmentWarningHeight;
    const remainingAlignmentsHeight = Math.min(middle, Math.max(75, middle - topAlignmentHeightAndWarning));
    const allottedTopRowAndWarning = middle - remainingAlignmentsHeight;

    // If the height we set is smaller than its content, then we will need to make the div scrollable. We
    // don't do this by default because when the container is scrollable, the dropdown and the options view
    // will not overflow out of the container. This is a rare case when dealing with a large alignment
    // on a short screen, see: T4704
    this.$('.fixed-top')
      .css('height', allottedTopRowAndWarning - alignmentWarningHeight)
      .toggleClass('fixed-top--overflow', topAlignmentHeightAndWarning <= allottedTopRowAndWarning);
    this.$('.alignments-container').css('top', allottedTopRowAndWarning);
    this.$('.alignments-container').css('bottom', bottom);
  }

  async save(): Promise<void> {
    // This logic is specific to Sequence alignments since Protein alignments cannot be edited.
    // It should be moved to SequenceAlignmentTabView since it's not shared.
    try {
      const analysis = (this.model as unknown as SequenceAlignmentTabState).get('analysis')!;
      const response = await analysis.save(null, {
        debounce: {namespace: 'updateJSON', wait: 1000},
        parse: false,
        fileUpdate: true,
        singleton: {namespace: 'updateJSON'},
        skipFileUpdateXHRCount: true,
        skipTimeoutXHR: true,
        url: analysis.getUpdateJSONURL(),
      });
      alignmentviewutil.notifySuccess(response);
    } catch (e) {
      if (shouldSuppressNotification(e)) {
        return;
      }
      if (net.isServerRequestError(e)) {
        alignmentviewutil.notifyError(e);
        return;
      }
      throw e;
    }
  }

  killListeners(): void {
    $(this.el).off('.alignment');
    this.stopListening();
    this.listenTo(this.model, 'change:loaded rerender', function loadRerender() {
      // @ts-expect-error
      return this.update();
    });
  }

  isLoaded(): boolean {
    return this.model.get('loaded');
  }

  linkMasterSequenceToActualSequence(): void {
    // This is a hook for SequenceAlignmentTabView.
  }

  updateStatsView(): void {
    // renderStatsView will be non-null when handling protein alignments.
    if (this.renderStatsView != null) {
      // renderStatsView() will return null when the mini-map / bottom bar is disabled.
      const renderedStatsView = this.renderStatsView();
      if (renderedStatsView) {
        renderToDOM(renderedStatsView, this.$('.statsView-container')[0]);
      } else {
        ReactDOM.unmountComponentAtNode(this.$('.statsView-container')[0]);
      }
    }
  }

  updateScroller(): void {
    // When the infinite scroller is activated, the alignment view will only be re-rendered when a scroll
    // event is triggered. In cases where the display window changes size without a scroll event, we
    // manually call the scroll handler to render content that was previously out of view.
    if (this.rowsView!.infiniteScroller) {
      this.rowsView!.infiniteScroller.handleScroll();
    }
  }

  maybeRenderBanners(): void {
    if (this.getBanners) {
      renderToDOM(this.getBanners(), this.$el.find('.alignment-warning')[0]);
    }
  }

  registerListeners(): void {
    this.listenTo(this.model.get('bufferViewLayout'), 'change:width change:height', () => {
      this.setMapDimensions();
      this.updateScroller();
    });
    this.listenTo(this.model.get('range')!, 'rangeChange', (redrawOnResize: boolean, _: boolean) => {
      this.showRange(redrawOnResize);
    });
    this.listenTo(this.model, 'scroll', (left: number) => {
      this.showRange(false, left);
    });
    this.listenTo(this.model, 'rowResize', this.debouncedResizeContainer);
    if (this.model.get('selection')) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.listenTo(this.model.get('selection'), 'change:start change:end', this.updateStatsView);
    }
    this.listenTo(this.model.get('visibilityOptions').getOption(vo.EXPAND_MINI_MAP.name), 'change', () => {
      this.update();
      this.updateScroller();
    });

    $(window).on(`resize.alignment-${this.cid}`, this.debouncedResizeContainer);
    $(this.el).on('wheel.alignment', (e: JQueryEventObject) => {
      const rx = -(e.originalEvent as WheelEvent).deltaX / 8;
      const ry = -(e.originalEvent as WheelEvent).deltaY / 8;
      // Only handle horizontal scrolling--the AlignmentRowsView will handle vertical scrolling
      if (Math.abs(rx) > Math.abs(ry)) {
        const range = this.model.get('range')!;
        range.panStart(range.start() - rx);
        this.showRange(false);
        e.preventDefault();
      }
    });

    // Remove old polymers and re-add new polymers for search.
    this.removePolymersForSearch();
    this.addPolymersForSearch();
  }

  update(): void {
    this.killListeners();

    if (!this.isLoaded()) {
      this.$el.find('.full-container').addClass('hidden');
      this.$el.find('.loading-screen').removeClass('hidden');
      return;
    }
    this.$el.find('.loading-screen').addClass('hidden');
    this.$el.find('.full-container').removeClass('hidden');

    // TODO: This should not be happening in the view.
    this.linkMasterSequenceToActualSequence();

    this.setMapDimensions(false);

    if (this.model.get('range')!.get('maxWidth') == null) {
      // Set range default.
      const targetFont = new Type(20, 0);
      const maxWidth = this.mapWidth();
      const span = Math.min(this.getPolymerLength(), targetFont.numChars(maxWidth));
      this.model.get('range')!.set({start: 0, end: span, maxWidth});
      this.model.get('range')!.correct();
    }
    this.fitFontToRange();

    // Render AlignmentRowsView
    if (this.rowsView == null) {
      this.rowsView = this.makeRowsView();
      this.rowsView.setElement(this.$('.alignments-container')[0]);
      this.rowsView.$el.addClass(this.rowsView.className);
      this.rowsView.render();
    } else {
      this.rowsView.update();
    }

    this.updateStatsView();
    this.renderSummaryMapView();

    // Render top AlignmentRowView
    if (this.topAlignmentView) {
      this.topAlignmentView.die();
    }
    this.topAlignmentView = this.makeTopAlignmentRowView();
    this.$el.find('.fixed-top .alignment-container').empty().append(this.topAlignmentView.render().el);

    this.maybeRenderBanners();

    // Resize SVG's
    this.resizeSVGs();

    // Only call showRange() if the element is already in the DOM. When render() is called on a
    // detached element (the normal case via TabContent.useLayoutEffect), resizeContainer() hasn't
    // run yet so container heights are 0 — rendering here is wasted work. afterAttach() handles
    // the real render once the element is inserted and resizeContainer() has correct dimensions.
    if (document.contains(this.el)) {
      const redraw = this.pendingFirstShow;
      this.pendingFirstShow = false;
      this.showRange(redraw);
    }

    // Register Listeners
    this.registerListeners();

    // Hack. Makes scrolling faster (by focusing for you).
    _.defer(() => $(this.topAlignmentView!.el).focus());
  }

  die(): void {
    $(this.el).off('.alignment');
    $(window).off(`resize.alignment-${this.cid}`);
    if (this.renderStatsView) {
      ReactDOM.unmountComponentAtNode(this.$('.statsView-container')[0]);
    }
    if (this.getBanners) {
      ReactDOM.unmountComponentAtNode(this.$el.find('.alignment-warning')[0]);
    }
    if (this.summaryMapView) {
      this.summaryMapView.stopListening();
    }
    if (this.rowsView) {
      this.rowsView.die();
    }
    if (this.topAlignmentView) {
      this.topAlignmentView.die();
    }
  }

  override remove(): this {
    // Stop listeners on children
    this.die();
    return super.remove();
  }

  fitFontToRange(): void {
    const type: Type = this.model.get('range')!.get('type');
    this.setType(type);
    this.setSliceValues();
  }

  resizeSVGs(): void {
    this.topAlignmentView!.setSVGWidth();
    this.rowsView!.resizeSVGs();
  }

  shouldCollapseMiniMap<T extends Model>(model: T): boolean {
    if (!model) {
      return false;
    }
    return !model.get('visibilityOptions').getOption(vo.EXPAND_MINI_MAP.name).get('visible');
  }

  showRange(redraw: boolean, left?: number): void {
    const startBP = this.model.get('range')!.start();
    if (redraw) {
      this.fitFontToRange();
      this.resizeSVGs();
    }

    this.scrollAnimator.base = startBP;

    this.topAlignmentView!.showRange(left);
    this.rowsView!.showRange(left);
  }

  mapWidth(): number {
    // This seems incredibly delicate and maybe wrong...
    return this.model.get('bufferViewLayout')!.get('width') - 110; // for scroll bar
  }

  setSliceValues(): void {
    const start = 0;
    const end = this.getPolymerLength();
    const type = this.getType();
    const width = type.width(end - start) + type.letterSpacing;
    const x: ScaleLinear<number, number> = d3
      .scaleLinear()
      .clamp(true)
      .domain([start, end])
      .range([0, width]);
    const xInv: ScaleLinear<number, number> = d3
      .scaleLinear()
      .clamp(true)
      .domain([0, width])
      .range([start, end]);

    this.model.get('slice')!.set({
      start,
      end,
      width,
      x,
      xInv,
      maxWidth: width,
      visibleWidth: this.mapWidth(),
    });
  }

  override render(): this {
    this.$el.empty();

    this.listenTo(this.model, 'change:loaded rerender', () => {
      this.update();
    });
    // eslint-disable-next-line new-cap
    this.$el.html(AlignmentTabTemplate());

    if (this.model.get('loaded')) {
      this.update();
    } else {
      this.resizeContainer();
    }
    setTestAttrs(this.$el, this.getDataTestAttrs());
    return this;
  }

  override afterAttach(): void {
    // Scroll alignments to correct location.
    if (this.model.get('loaded')) {
      this.pendingFirstShow = false;
      this.resizeContainer();
      this.showRange(true);
    }
  }

  renderSummaryMapView(): void {
    const summaryMapViewContainer = this.$el.find('.fixed-bottom .summaryMapView-container');
    if (this.summaryMapView != null) {
      this.summaryMapView.stopListening();
      summaryMapViewContainer.empty();
    }
    this.summaryMapView = this.makeSummaryMapView();
    if (this.summaryMapView) {
      summaryMapViewContainer.append(this.summaryMapView.render().el);
    }
  }
}
PolymerAlignmentTabView.initClass();
